import { type ITagOption, Tags as UIKitTags } from '@cian/ui-kit';
import { type FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { trackGeoTagDeleted, trackClearButtonClick, parseTagValue } from '../../../packages/Tags';
import { useAppDispatch, useFilterChangeCallback } from 'shared/hooks';
import { selectAreTagsHidden, selectTags, selectTagsToReset } from 'shared/selectors/tags';
import { removeTag } from 'shared/thunks/tags';
import { setTags } from 'shared/slices/tags';

import { ClearTagsAdornment, ExpandCollapseAdornment } from './adornments';
import { getTagIcon } from './utils';

import { selectPageType } from 'shared/selectors/page/selectPageType';
import { EPageType } from 'shared/types/page';
import { TagsLayout } from 'shared/components/TagsLayout/TagsLayout';

const GEO_SPECIAL_TYPES_TAGS = [
  'geoLocation',
  'geoDistrict',
  'geoStreet',
  'geoUnderground',
  'geoRailway',
  'geoNewbuilding',
];

const displayModeOptions = {
  maxLines: 1,
  expandCollapseAdornment: (isExpanded: boolean) => <ExpandCollapseAdornment isExpanded={isExpanded} />,
  isFoldable: true,
  isFolded: true,
};

export const TagsContainer: FC = () => {
  const dispatch = useAppDispatch();
  const tags = useSelector(selectTags);
  const tagsToReset = useSelector(selectTagsToReset);
  const areTagsHidden = useSelector(selectAreTagsHidden);
  const pageType = useSelector(selectPageType);

  const options = useMemo<ITagOption[]>(
    () =>
      tags.map(tag => ({
        label: tag.label,
        value: tag.value,
        icon: getTagIcon(tag.leftAdornment),
        dataTestId: 'Tag',
      })),
    [tags],
  );

  const onTagDeleted = useCallback(
    (tag: ITagOption) => {
      const [tagType] = parseTagValue(tag.value);

      /**
       * Если это гео тэг улицы, района, шоссе, региона или ЖД - отправляем аналитику
       */
      if (GEO_SPECIAL_TYPES_TAGS.includes(tagType)) {
        trackGeoTagDeleted(tagType);
      }

      dispatch(removeTag(tag));
    },
    [dispatch],
  );

  const onChange = useFilterChangeCallback('geo');

  const onAllTagsDeleted = useCallback(() => {
    trackClearButtonClick(false);
    dispatch(setTags([]));
    onChange({ action: 'clearTags', arguments: [tagsToReset] });
  }, [dispatch, onChange, tagsToReset]);

  if (areTagsHidden || !options.length || pageType === EPageType.MAIN) {
    return null;
  }

  return (
    <TagsLayout isSearchpage={pageType === EPageType.SEARCH}>
      <UIKitTags
        options={options}
        onClose={onTagDeleted}
        displayModeOptions={displayModeOptions}
        rightAdornment={<ClearTagsAdornment tagsNumber={options.length} onAllTagsDeleted={onAllTagsDeleted} />}
      />
    </TagsLayout>
  );
};
